import type { CSSProperties, ReactNode } from "react";

/**
 * HxSkeleton — the loading state for every /ops surface.
 *
 * A page that is fetching shows the SHAPE of what is coming, not the word
 * "Loading…": the eye lands where the content will be and the layout does not
 * jump when data arrives. Pick the variant that matches the surface:
 *
 *   page    filter bar + KPI cards + two panels — a dashboard-shaped default
 *   cards   a grid of KPI/stat cards only
 *   table   rows of cells — drop it inside `<td colSpan={n} style={{padding:0}}>`
 *   list    stacked rows with a title + subtitle line (inboxes, pickers)
 *   detail  an avatar/heading head plus body lines (right rails, drawers)
 *   block   one bar — for an inline value that is still on its way
 *
 * Every bar is `.hx-skel`, whose fill and shimmer come from the theme tokens,
 * so it reads correctly in light and dark. The wrapper is `role="status"`
 * with `aria-busy`, so assistive tech hears one "Loading" instead of a pile
 * of decorative boxes (the bars themselves are aria-hidden).
 */
export type HxSkeletonVariant = "page" | "cards" | "table" | "list" | "detail" | "block";

interface Props {
  variant?: HxSkeletonVariant;
  /** Row count for table / list / detail-lines; card count for cards / page. */
  rows?: number;
  /** Cell count per table row, or column count for the cards grid. */
  cols?: number;
  /** block only. */
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: CSSProperties;
  /** Accessible name announced while busy. */
  label?: string;
}

function Bar({ w, h, style }: { w: number | string; h: number; style?: CSSProperties }) {
  return <span aria-hidden="true" className="hx-skel" style={{ display: "block", width: w, height: h, ...style }} />;
}

/** A plausible, non-uniform width for line `i` so a block of lines reads as text. */
function lineWidth(i: number): string {
  return ["92%", "78%", "55%", "85%", "64%"][i % 5];
}

export function HxSkeleton({
  variant = "page",
  rows,
  cols,
  width,
  height,
  className,
  style,
  label = "Loading",
}: Props) {
  if (variant === "block") {
    return (
      <span
        role="status"
        aria-busy="true"
        aria-label={label}
        className={"hx-skel" + (className ? " " + className : "")}
        style={{ display: "inline-block", width: width ?? "60%", height: height ?? 12, ...style }}
      />
    );
  }

  let body: ReactNode;
  switch (variant) {
    case "table": {
      const n = rows ?? 6;
      const c = cols ?? 5;
      body = (
        <div className="hx-skel-rows">
          {Array.from({ length: n }).map((_, r) => (
            <div key={r} className="hx-skel-trow">
              {Array.from({ length: c }).map((_, i) => (
                <Bar key={i} w={i === 0 ? "18%" : i === c - 1 ? "10%" : "22%"} h={10} />
              ))}
            </div>
          ))}
        </div>
      );
      break;
    }
    case "list": {
      const n = rows ?? 6;
      body = (
        <div className="hx-skel-rows">
          {Array.from({ length: n }).map((_, r) => (
            <div key={r} className="hx-skel-row">
              <Bar w={r % 2 ? "52%" : "64%"} h={11} />
              <Bar w={r % 3 ? "38%" : "44%"} h={9} />
            </div>
          ))}
        </div>
      );
      break;
    }
    case "detail": {
      const n = rows ?? 6;
      body = (
        <div className="hx-skel-detail">
          <div className="hx-skel-detail-head">
            <span aria-hidden="true" className="hx-skel" style={{ display: "block", width: 36, height: 36, borderRadius: "50%", flex: "none" }} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              <Bar w="40%" h={13} />
              <Bar w="65%" h={10} />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 14 }}>
            {Array.from({ length: n }).map((_, i) => (
              <Bar key={i} w={lineWidth(i)} h={10} />
            ))}
          </div>
        </div>
      );
      break;
    }
    case "cards": {
      const n = rows ?? 4;
      body = <CardGrid count={n} cols={cols ?? Math.min(4, n)} />;
      break;
    }
    case "page":
    default: {
      body = (
        <>
          <div className="hx-skel-filter">
            <Bar w={140} h={28} style={{ borderRadius: 7 }} />
            <Bar w={110} h={28} style={{ borderRadius: 7 }} />
            <Bar w={90} h={28} style={{ borderRadius: 7 }} />
          </div>
          <CardGrid count={rows ?? 4} cols={cols ?? 4} />
          <div className="hx-skel-panels">
            {[0, 1].map((p) => (
              <div key={p} className="hx-skel-card" style={{ minHeight: 220 }}>
                <Bar w="38%" h={12} />
                <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 6 }}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Bar key={i} w={lineWidth(i + p)} h={10} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }
  }

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className={"hx-skel-wrap hx-skel-" + variant + (className ? " " + className : "")}
      style={style}
    >
      {body}
    </div>
  );
}

function CardGrid({ count, cols }: { count: number; cols: number }) {
  return (
    <div className="hx-skel-cards" style={{ ["--hx-skel-cols" as string]: cols } as CSSProperties}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="hx-skel-card">
          <Bar w="45%" h={10} />
          <Bar w="60%" h={26} style={{ borderRadius: 6 }} />
          <Bar w="80%" h={9} />
        </div>
      ))}
    </div>
  );
}
